import React from 'react';
import { ZenInputText } from '../../components/form';
import { Edit, Preview, Hide } from '@menuzen/icon/build/outline';
import EditSiteUrlModal from './modal/EditSiteUrlModal';
import UnpublishModal from './modal/UnpublishModal';
import classNames from 'classnames';
import PublishModal from './modal/PublishModal';
import config from '../../config/config';
import { noop } from 'lodash';
import Dialog, { DialogProps } from '../../components/dialog/Dialog';
import { useQueryParam } from '../../common/hooks/use-query-param';
import { Account } from '../../shared/types/account';
import client from '../../common/apollo/client';
import { SiteDesign } from '../../shared/types/site/design';

const MODAL_SETTING = {
    EDIT_URL: 'EDIT_URL',
    PUBLISH: 'PUBLISH',
    UNPUBLISH: 'UNPUBLISH',
    DELETE: 'DELETE',
};
interface SiteSettingModal extends DialogProps {
    account: Account;
    design: SiteDesign;
}
const SiteSettingModal: React.FC<SiteSettingModal> = ({ account, design, onClose, ...props }) => {
    const [subModal, setSubModal] = useQueryParam<string | null>('sub_modal');

    return (
        <Dialog anchor="bottom" title={'Site Settings'} showClose={true} onClose={onClose} {...props}>
            <div className="mt-10 px-28 pb-40">
                <>
                    <div className="pb-25 border-b border-primary-40">
                        <p className="text-14 leading-17 mb-12 text-primary-80">Site URL</p>
                        <div className="flex items-center">
                            <ZenInputText
                                prefix={
                                    <div className="whitespace-nowrap overflow-hidden pl-12 pr-10 font-semibold text-14 text-primary-50">
                                        {`${config.liveBioDomain}/`}
                                    </div>
                                }
                                size={`sm`}
                                value={account?.username || ''}
                                onChange={() => noop}
                                disabled
                                className="bg-primary-10 text-primary-50"
                                inputClass={'bg-primary-10'}
                            />
                            <div
                                className="text-20 w-45 flex items-center justify-center"
                                onClick={() => setSubModal(MODAL_SETTING.EDIT_URL)}
                            >
                                <Edit />
                            </div>
                        </div>
                    </div>
                    <div className="pb-16 border-b border-primary-40 mt-25">
                        <p className="text-14 leading-17 mb-20 text-primary-80">Visibility</p>
                        <div
                            className={classNames({
                                'rounded-5 border px-15 py-12 mb-10': true,
                                'border-teal bg-started': design?.publishedAt,
                                'cursor-pointer border-white bg-white text-primary-80': !design?.publishedAt,
                            })}
                            onClick={() => !design?.publishedAt && setSubModal(MODAL_SETTING.PUBLISH)}
                        >
                            <div className="flex items-center justify-start text-16">
                                <Preview />
                                <p className="ml-5">Published</p>
                            </div>
                            <p className="mt-5 text-12 leading-14">Published and visible on the web.</p>
                        </div>
                        <div
                            className={classNames({
                                'cursor-pointer rounded-5 border px-15 py-12 mb-10': true,
                                'border-teal bg-started': !design?.publishedAt,
                                'cursor-pointer border-white bg-white text-primary-80': design?.publishedAt,
                            })}
                            onClick={() => design?.publishedAt && setSubModal(MODAL_SETTING.UNPUBLISH)}
                        >
                            <div className="flex items-center justify-start text-16">
                                <Hide />
                                <p className="ml-5">Unpublished</p>
                            </div>
                            <p className="mt-5 text-12 leading-14">Unpublished and not visible on the web.</p>
                        </div>
                    </div>
                    <EditSiteUrlModal
                        username={account.username}
                        title="Edit Site URL"
                        open={subModal === MODAL_SETTING.EDIT_URL}
                        onClose={() => setSubModal(null)}
                        showClose={true}
                        onSuccess={() => {
                            setSubModal(null);
                            client.refetchQueries({
                                include: ['GetAccount'],
                            });
                        }}
                    />
                    <UnpublishModal
                        title="Unpublish Site"
                        open={subModal === MODAL_SETTING.UNPUBLISH}
                        onClose={() => setSubModal(null)}
                        showClose={true}
                        onSuccess={() => {
                            setSubModal(null);
                            client.refetchQueries({
                                include: ['GetAccount'],
                            });
                        }}
                    />
                    <PublishModal
                        title="Publish Site"
                        open={subModal === MODAL_SETTING.PUBLISH}
                        onClose={() => {
                            setSubModal(null);
                        }}
                        showClose={true}
                        onSuccess={() => {
                            client.refetchQueries({
                                include: ['GetAccount'],
                            });
                        }}
                        username={account.username}
                    />
                </>
            </div>
        </Dialog>
    );
};

export default SiteSettingModal;
